/**
 * App state: who is signed in, and the lists the sidebar and menus draw from.
 *
 * One context rather than a library. The sidebar, the composer's menus, the
 * project page and settings all read the same `me`, chat list, project list,
 * model catalogue and skills, and any of them can ask for a refresh after a
 * write.
 *
 * `status` decides which screen renders at all:
 *   loading      the first /me is in flight
 *   signedOut    401 — send them to mauvely.com/signin
 *   secondFactor 403 second_factor_required — send them to finish TOTP
 *   unreachable  the Worker did not answer, or answered 5xx
 *   ready        `me` is set. `me.ai.allowed` false is still ready: the app
 *                renders the off screen, not an error
 *
 * Once AI is allowed, the account's hub (hub.ts) keeps the chat list current:
 * a chat made, renamed, titled or deleted on another device, or a reply begun
 * or ended there, arrives as it happens.
 *
 * An agent's chat is never listed: whoever reports it, it goes to its
 * parent's agents (`useAgents`), which the chat page loads with the chat.
 * Nor is a PC's Dispatch thread: it is that PC's `lastUsedAt` instead, and the
 * `devices` list is what Dispatch draws. In Assist's own window on a PC the
 * hub is opened as the PC, once it has published (hooks/useDeviceRunner.ts).
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  ApiError,
  getMe,
  getModels,
  listChats,
  listDevices,
  listProjects,
  listSkills,
  patchSettings,
  type ChatSummary,
  type Device,
  type Me,
  type ModelCatalogue,
  type ProjectSummary,
  type Skill
} from './api';
import { putAgent, withoutChat, type AgentLists } from './hooks/agentLists';
import { putDevice, sortDevices, withOnline, withoutDevice, withThread } from './hooks/deviceLists';
import { isTemporary } from './hooks/temporaryChats';
import { useDeviceRunner } from './hooks/useDeviceRunner';
import { startHub } from './hub';
import { resyncVideos, videoEnded } from './hooks/useGallery';

export type Status = 'loading' | 'signedOut' | 'secondFactor' | 'unreachable' | 'ready';

interface AppState {
  status: Status;
  /** The details of the error that set `status` — `signInUrl` on a 401,
   *  `twoFactorUrl` on a second-factor 403 — so the gate can send the person
   *  where the Worker says rather than guessing. Empty otherwise. */
  errorDetails: Record<string, unknown>;
  me: Me | null;
  chats: ChatSummary[];
  projects: ProjectSummary[];
  catalogue: ModelCatalogue | null;
  /** The person's skills, for the composer's slash menu and Settings. Null
   *  until they have loaded. */
  skills: Skill[] | null;
  /** Re-read /me — after onboarding, a settings change, or a turn's usage. */
  refreshMe: () => Promise<void>;
  /** Re-read the chat and project lists. */
  refreshLists: () => Promise<void>;
  /** Re-read the model catalogue — after a provider key changes. */
  refreshModels: () => Promise<void>;
  /** Replace `me` locally without a round trip, e.g. with a turn's usage. */
  patchMe: (fn: (me: Me) => Me) => void;
  /** The account's PCs, the latest used first. Null until they have loaded. */
  devices: Device[] | null;
  /** A PC's thread changed (the hub's `dispatch`), or `chatId` null: read
   *  every open thread again, after the hub came back. `seq` is new each time. */
  thread: { chatId: string | null; seq: number };
  /** Read the PCs again. */
  refreshDevices: () => Promise<void>;
  /** A PC forgotten, here or on another page. */
  removeDevice: (id: string) => void;
  /** In the desktop app, the Worker's reason for not having this PC as one
   *  of the account's; null when it has it, or this is no PC. */
  pcRefused: string | null;
  /** Each chat's agents, oldest first, by the chat's id: those loaded with
   *  it, and those reported since. */
  agents: Record<string, ChatSummary[]>;
  setAgents: (parentId: string, agents: ChatSummary[]) => void;
  /** Put one chat at the top of the list, or update it in place. An agent
   *  goes to its parent's agents instead. */
  upsertChat: (chat: ChatSummary) => void;
  /** Move a chat to the top with a new time, keeping the title the list
   *  already has — a rename made while a reply streamed stays put. */
  touchChat: (chat: ChatSummary) => void;
  removeChat: (id: string) => void;
  /** Add a skill to the list, or update it in place. */
  upsertSkill: (skill: Skill) => void;
  removeSkill: (id: string) => void;
}

const Ctx = createContext<AppState | null>(null);

export function AppStateProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [errorDetails, setErrorDetails] = useState<Record<string, unknown>>({});
  const [me, setMe] = useState<Me | null>(null);
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [catalogue, setCatalogue] = useState<ModelCatalogue | null>(null);
  const [skills, setSkills] = useState<Skill[] | null>(null);
  const [agents, setAgentsMap] = useState<AgentLists>({});
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [thread, setThread] = useState<{ chatId: string | null; seq: number }>({ chatId: null, seq: 0 });

  const refreshMe = useCallback(async () => {
    try {
      const next = await getMe();
      setMe(next);
      setErrorDetails({});
      setStatus('ready');
    } catch (err) {
      setErrorDetails(err instanceof ApiError ? err.details : {});
      if (err instanceof ApiError && err.status === 401) setStatus('signedOut');
      else if (err instanceof ApiError && err.code === 'second_factor_required') setStatus('secondFactor');
      else setStatus('unreachable');
    }
  }, []);

  const refreshLists = useCallback(async () => {
    const [c, p] = await Promise.all([
      listChats().catch(() => null),
      listProjects().catch(() => null)
    ]);
    if (c) setChats(c.chats);
    if (p) setProjects(p.projects);
  }, []);

  const refreshDevices = useCallback(async () => {
    const d = await listDevices().catch(() => null);
    setDevices((was) => (d ? sortDevices(d.devices) : (was ?? [])));
  }, []);

  const refreshModels = useCallback(async () => {
    const m = await getModels().catch(() => null);
    if (m) setCatalogue(m);
  }, []);

  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  // The lists only exist once AI is allowed — every route but /me refuses
  // otherwise — so they load after /me says so.
  const allowed = status === 'ready' && !!me?.ai.allowed;
  useEffect(() => {
    if (!allowed) return;
    void refreshLists();
    void refreshDevices();
    void refreshModels();
    // Once: Settings → Skills changes the list in place.
    listSkills()
      .then((r) => setSkills(r.skills))
      .catch(() => setSkills([]));
  }, [allowed, refreshLists, refreshDevices, refreshModels]);

  // A new automation runs in the zone this browser reports, until one is set.
  const zoneUnset = allowed && !!me && !me.settings.timeZone;
  useEffect(() => {
    if (!zoneUnset) return;
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!timeZone) return;
    patchSettings({ timeZone })
      .then((settings) => setMe((m) => (m ? { ...m, settings } : m)))
      .catch(() => {});
  }, [zoneUnset]);

  const patchMe = useCallback((fn: (m: Me) => Me) => setMe((m) => (m ? fn(m) : m)), []);

  const setAgents = useCallback((parentId: string, list: ChatSummary[]) => {
    setAgentsMap((m) => ({ ...m, [parentId]: list }));
  }, []);

  const upsertAgent = useCallback((chat: ChatSummary) => setAgentsMap((m) => putAgent(m, chat)), []);

  // A PC's thread is never listed as a chat: it makes its PC the latest used.
  const noteThread = useCallback((chat: ChatSummary) => setDevices((list) => list && withThread(list, chat)), []);

  const removeDevice = useCallback((id: string) => setDevices((list) => list && withoutDevice(list, id)), []);

  // A temporary chat is never listed, whoever reports it: Home once it is
  // made, a turn when it is done or titled, the chat page after a branch.
  // A summary the page made itself says nothing of a reply running, and is
  // made only once none is, so it lists none.
  const upsertChat = useCallback((chat: ChatSummary) => {
    if (chat.agent) return upsertAgent(chat);
    if (chat.deviceId) return noteThread(chat);
    if (isTemporary(chat.id)) return;
    const row = { ...chat, live: chat.live ?? null };
    setChats((list) => [row, ...list.filter((c) => c.id !== chat.id)].sort((a, b) => b.updatedAt - a.updatedAt));
  }, []);

  const touchChat = useCallback((chat: ChatSummary) => {
    if (chat.agent) return upsertAgent(chat);
    if (chat.deviceId) return noteThread(chat);
    if (isTemporary(chat.id)) return;
    setChats((list) => {
      const current = list.find((c) => c.id === chat.id);
      const next = { ...chat, live: chat.live ?? null, ...(current ? { title: current.title } : {}) };
      return [next, ...list.filter((c) => c.id !== chat.id)].sort((a, b) => b.updatedAt - a.updatedAt);
    });
  }, []);

  // A chat's agents go with it.
  const removeChat = useCallback((id: string) => {
    setChats((list) => list.filter((c) => c.id !== id));
    setAgentsMap((m) => withoutChat(m, id));
  }, []);

  const upsertSkill = useCallback((skill: Skill) => {
    setSkills((list) =>
      list?.some((s) => s.id === skill.id) ? list.map((s) => (s.id === skill.id ? skill : s)) : [...(list ?? []), skill]
    );
  }, []);

  const removeSkill = useCallback((id: string) => {
    setSkills((list) => (list ?? []).filter((s) => s.id !== id));
  }, []);

  // The page's own turns, for the PC's runner to take replies on through.
  const turnApp = useMemo(() => ({ upsertChat, touchChat, patchMe, refreshMe }), [upsertChat, touchChat, patchMe, refreshMe]);
  const { pc, refused: pcRefused, frame: pcFrame } = useDeviceRunner(allowed, turnApp);
  // The PC's own page was published after its list was read, and before its
  // hub was open to hear of it.
  useEffect(() => {
    if (pc) void refreshDevices();
  }, [pc, refreshDevices]);

  // Not in the assistant sheet, which shows one reply and closes. In Assist's
  // own window on a PC, not before the PC has published.
  useEffect(() => {
    if (!allowed || location.pathname.startsWith('/assistant') || pc === undefined) return;
    return startHub((frame) => {
      if (frame.t === 'chat') upsertChat(frame.chat);
      else if (frame.t === 'gone') removeChat(frame.chatId);
      else if (frame.t === 'lists') void refreshLists();
      else if (frame.t === 'video') videoEnded(frame);
      else if (frame.t === 'dispatch') {
        noteThread(frame.chat);
        setThread((t) => ({ chatId: frame.chat.id, seq: t.seq + 1 }));
        pcFrame(frame);
      } else if (frame.t === 'device') setDevices((list) => putDevice(list ?? [], frame.device));
      else if (frame.t === 'device_online') setDevices((list) => list && withOnline(list, frame.deviceId, frame.online, Math.floor(Date.now() / 1000)));
      else if (frame.t === 'device_gone') {
        removeDevice(frame.deviceId);
        pcFrame(frame);
      }
      else if (frame.t === 'wake' || frame.t === 'cancel' || frame.t === 'pair' || frame.t === 'approval') pcFrame(frame);
    }, () => {
      void refreshLists();
      void refreshDevices();
      void resyncVideos();
      setThread((t) => ({ chatId: null, seq: t.seq + 1 }));
    }, pc ?? undefined);
    // The callbacks are stable; the hub opens once per sign-in, and again as
    // the PC if its first publish was late.
  }, [allowed, pc]);

  const value = useMemo<AppState>(
    () => ({
      status,
      errorDetails,
      me,
      chats,
      projects,
      catalogue,
      skills,
      devices,
      thread,
      refreshDevices,
      removeDevice,
      pcRefused,
      agents,
      setAgents,
      refreshMe,
      refreshLists,
      refreshModels,
      patchMe,
      upsertChat,
      touchChat,
      removeChat,
      upsertSkill,
      removeSkill
    }),
    [status, errorDetails, me, chats, projects, catalogue, skills, devices, thread, refreshDevices, removeDevice, pcRefused, agents, setAgents, refreshMe, refreshLists, refreshModels, patchMe, upsertChat, touchChat, removeChat, upsertSkill, removeSkill]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useApp used outside AppStateProvider');
  return ctx;
}

const NONE: ChatSummary[] = [];

/** A chat's agents, oldest first. */
export const useAgents = (parentId: string): ChatSummary[] => useApp().agents[parentId] ?? NONE;
